import * as traits from '../hooks/utils/segmentTraitGetters'
import type { Account } from '../types'
import { env } from '../../env'

function identifySegment(user: Account): void {
  if (window.analytics) {
    if (user.id && !user.isImpersonation && user.currentOrganization) {
      const currentOrganization = user.currentOrganization
      const userTraits = {
        createdAt: user.createdAt,
        currentAnalyzePlan: traits.currentAnalyzePlan({ currentOrganization }),
        currentBufferPlan: traits.currentBufferPlan(user),
        currentPublishPlan: traits.currentPublishPlan({ currentOrganization }),
        email: user.email,
        isMultiProductCustomer: traits.isMultiProductCustomer({
          currentOrganization,
        }),
        isOneBufferEnabled: user.currentOrganization?.isOneBufferOrganization,
        isPayingAnalyzeUser: traits.isPayingAnalyzeOrganization(user),
        isPayingBufferUser: traits.isPayingBufferOrganization({
          currentOrganization,
        }),
        isPayingPublishUser: traits.isPayingPublishOrganization(user),
        isFreePlan: traits.isFreePlan(user),
        currentOrganizationId: user.currentOrganization?.id,
        organizationUserRole: traits.organizationUserRole({
          currentOrganization,
        }),

        isOnBufferTrial: traits.isOnBufferTrial({ currentOrganization }),
        currentBufferTrialPlan: traits.currentBufferTrialPlan(user),

        isOnPublishTrial: traits.isOnPublishTrial({ currentOrganization }),
        currentPublishTrialPlan: traits.currentPublishTrialPlan({
          currentOrganization,
        }),

        isOnAnalyzeTrial: traits.isOnAnalyzeTrial({ currentOrganization }),
        currentAnalyzeTrialPlan: traits.currentAnalyzeTrialPlan({
          currentOrganization,
        }),

        billingCycle: traits.billingCycle({ currentOrganization }),
        trialBillingCycle: traits.trialBillingCycle({ currentOrganization }),
        isBetaEnabled: user.currentOrganization?.isBetaEnabled,
        isAlphaEnabled: user.currentOrganization?.isAlphaEnabled,
        appCommitHash: env.VITE_COMMIT_HASH,
      }
      window.analytics.identify(user.id, userTraits)
      // Identify in Customer.io to ensure In-App Messaging is tracked
      if (window.cioanalytics && window.cioanalytics.identify) {
        window.cioanalytics.identify({
          ...userTraits,
          id: user.id,
        })
      }
    }
  }
}

export function identifyUser(user: Account): void {
  identifySegment(user)
}

export function groupUser(user: Account): void {
  if (window.analytics) {
    if (
      user.id &&
      !user.isImpersonation &&
      user.currentOrganization &&
      user.currentOrganization.id
    ) {
      const currentOrganization = user.currentOrganization
      window.analytics.group(user.currentOrganization.id, {
        currentAnalyzePlan: traits.currentAnalyzePlan({ currentOrganization }),
        currentBufferPlan: traits.currentBufferPlan(user),
        currentPublishPlan: traits.currentPublishPlan({ currentOrganization }),
        isOneBufferEnabled: user.currentOrganization.isOneBufferOrganization,
        isPayingAnalyzeOrganization: traits.isPayingAnalyzeOrganization(user),
        isPayingBufferOrganization: traits.isPayingBufferOrganization({
          currentOrganization,
        }),
        isPayingPublishOrganization: traits.isPayingPublishOrganization(user),
        name: user.currentOrganization.name,
        organizationId: user.currentOrganization.id,

        isOnBufferTrial: traits.isOnBufferTrial({ currentOrganization }),
        currentBufferTrialPlan: traits.currentBufferTrialPlan(user),

        isOnPublishTrial: traits.isOnPublishTrial({ currentOrganization }),
        currentPublishTrialPlan: traits.currentPublishTrialPlan({
          currentOrganization,
        }),

        isOnAnalyzeTrial: traits.isOnAnalyzeTrial({ currentOrganization }),
        currentAnalyzeTrialPlan: traits.currentAnalyzeTrialPlan({
          currentOrganization,
        }),

        isBetaEnabled: user.currentOrganization.isBetaEnabled,
        isAlphaEnabled: user.currentOrganization.isAlphaEnabled,
      })
    }
  }
}

// prettier-ignore
function injectSegmentTracker(): void {
  if (!window.analytics) {
    if (env.SEGMENT_KEY) {
      // This is the Segment snippet https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/quickstart/#step-2-copy-the-segment-snippet
      /* eslint-disable */
      // @ts-expect-error TS(1345) FIXME: An expression of type 'void' cannot be tested for ... Remove this comment to see the full error message
      !(function () {
        var analytics = (window.analytics = window.analytics || [])
        if (!analytics.initialize) {
          if (analytics.invoked) {
            window.console &&
              console.error &&
              console.error('Segment snippet included twice.')
          } else {
            analytics.invoked = !0
            analytics.methods = [
              'trackSubmit',
              'trackClick',
              'trackLink',
              'trackForm',
              'pageview',
              'identify',
              'reset',
              'group',
              'track',
              'ready',
              'alias',
              'debug',
              'page',
              'once',
              'off',
              'on',
              'addSourceMiddleware',
              'addIntegrationMiddleware',
              'setAnonymousId',
              'addDestinationMiddleware',
            ]
            analytics.factory = function (t: any) {
              return function () {
                var e = Array.prototype.slice.call(arguments)
                e.unshift(t)
                analytics.push(e)
                return analytics
              }
            }
            for (var t = 0; t < analytics.methods.length; t++) {
              var e = analytics.methods[t]
              analytics[e] = analytics.factory(e)
            }
            analytics.load = function (t: any, e: any) {
              var n = document.createElement('script')
              n.type = 'text/javascript'
              n.async = !0
              n.src =
                'https://cdn.segment.com/analytics.js/v1/' +
                t +
                '/analytics.min.js'
              var a = document.getElementsByTagName('script')[0]
              // @ts-expect-error TS(2531) FIXME: Object is possibly 'null'.
              a.parentNode.insertBefore(n, a)
              analytics._loadOptions = e
            }
            analytics.SNIPPET_VERSION = '4.1.0'
            analytics.load(env.SEGMENT_KEY)
            analytics.page()
            /* eslint-enable */
          }
        }
      })()
    } else {
      window.console.warn('Please provide a SEGMENT_KEY, to start tracking')
    }
  }
}

function trackUser(user: Account): void {
  injectSegmentTracker()
  identifyUser(user)
  groupUser(user)
}

export default trackUser
